import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  //   faPlus,
  faStar,
  faCheck,
  faExclamationTriangle,
  faSpinner,
  faArrowUpRightFromSquare,
} from "@fortawesome/free-solid-svg-icons"
import { useState } from "react"
import { useParams } from "react-router-dom"
import * as yup from "yup"
import { Formik, Form, Field, ErrorMessage } from "formik"
import { useEducationalVideo } from "../../hooks/useEducationalVideo"
import { useSubmitCustomerFeedback } from "../../hooks/useCustomerFeedback"

const feedbackSchema = yup.object().shape({
  experience: yup
    .number()
    .required("Experience rating is required")
    .min(1, "Experience rating must be at least 1")
    .max(5, "Experience rating must be at most 5")
    .integer("Experience rating must be a whole number"),
  report_clarity: yup
    .number()
    .required("Report clarity rating is required")
    .min(1, "Report clarity rating must be at least 1")
    .max(5, "Report clarity rating must be at most 5")
    .integer("Report clarity rating must be a whole number"),
  comments: yup
    .string()
    .max(500, "Comments must be 500 characters or less")
    .optional(),
})

const StarRating = ({ name, value, onChange, label }) => {
  const [hoverValue, setHoverValue] = useState(0)

  return (
    <div className="flex justify-between">
      <label className="text-left text-sm font-medium text-gray-700">
        {label}
      </label>
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <FontAwesomeIcon
            key={star}
            className={`w-6 h-6 cursor-pointer transition-colors ${
              star <= (hoverValue || value) ? "text-blue-500" : "text-gray-300"
            }`}
            icon={faStar}
            onClick={() => onChange(name, star)}
            onMouseEnter={() => setHoverValue(star)}
            onMouseLeave={() => setHoverValue(0)}
          />
        ))}
      </div>
    </div>
  )
}

const EducationalVideos = () => {
  const { uuid } = useParams()
  const {
    data: educationalVideoData,
    isLoading: isEducationalVideoDataLoading,
    error,
  } = useEducationalVideo(uuid)

  const { mutateAsync: submitFeedback, isPending: isFeedbackSubmitting } =
    useSubmitCustomerFeedback()

  //   const [expandedItems, setExpandedItems] = useState(new Set())
  const [isSubmitted, setIsSubmitted] = useState(false)

  //   const toggleExpanded = (index) => {
  //     const newExpandedItems = new Set(expandedItems)
  //     if (newExpandedItems.has(index)) {
  //       newExpandedItems.delete(index)
  //     } else {
  //       newExpandedItems.add(index)
  //     }
  //     setExpandedItems(newExpandedItems)
  //   }

  //   const FAQs = [
  //     {
  //       question: "What does it mean when my MRI shows a herniated disc?",
  //       answer:
  //         "A herniated disc means some of the soft, gel-like material inside a spinal disc has pushed through its outer layer. This is very common and doesn't always cause symptoms. When it does, pain or numbness typically improves over weeks to months with appropriate treatment. Your referring physician can recommend the right next steps based on your specific findings.",
  //     },
  //     {
  //       question: "Is a second opinion on my MRI a good idea?",
  //       answer:
  //         "A second opinion is always reasonable — especially for complex findings or when you're considering surgery. Expert Radiology offers a formal second opinion service where a board-certified subspecialist reviews your images and provides an independent written report, typically within 24–48 hours.",
  //     },
  //     {
  //       question: "What is sciatica and how is it treated?",
  //       answer:
  //         "Sciatica is pain that radiates along the sciatic nerve — from the lower back, through the hip, and down the leg. It's often caused by a herniated disc or bone spur pressing on the nerve. Most cases resolve with physical therapy, anti-inflammatory medications, and time. Surgery is considered only when conservative treatments haven't worked after several weeks.",
  //     },
  //     {
  //       question: "Who reads my MRI report?",
  //       answer:
  //         "Your report was interpreted by a board-certified radiologist at Expert Radiology — a subspecialty-trained physician who focuses exclusively on reading and interpreting medical imaging. All reports are reviewed under our quality framework before delivery.",
  //     },
  //     {
  //       question: "How do I speak with someone about my results?",
  //       answer:
  //         "Please contact the ordering physician or facility that sent your MRI for a full clinical consultation. They have access to your complete report and are best positioned to explain how these findings relate to your symptoms and treatment options.",
  //     },
  //   ]

  return (
    <div className="-my-4">
      <header className="bg-primary text-white text-center py-5">
        <div className="border border-gray-600 px-3 py-1 inline-block rounded-full uppercase text-[10px] md:text-xs text-[#60A5FA] font-medium tracking-wide backdrop-blur-sm bg-white/10">
          Your Personalized Report
        </div>
        <h1 className="text-2xl md:text-4xl font-extrabold tracking-wide mb-4">
          <span className="text-white">Understanding Your</span>
          <br />
          <span className="text-[#60A5FA]">MRI Findings</span>
        </h1>
        <p className="text-[11px] md:text-sm leading-relaxed text-gray-400 font-medium">
          Your radiologist identified findings worth explaining in plain
          language.
          <br />
          These short videos will help you understand what was seen and what it
          <br />
          means for you.
        </p>
      </header>

      {!uuid ||
        (!educationalVideoData && !isEducationalVideoDataLoading && !error && (
          <div className="flex items-center justify-center min-h-20">
            <p className="text-gray-600/80 italic text-base">
              No video available or invalid ID. If you believe this is an error,
              please contact support.
            </p>
          </div>
        ))}

      {isEducationalVideoDataLoading && (
        <div className="flex items-center justify-center min-h-40">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading educational videos...</p>
          </div>
        </div>
      )}

      {error && (
        <div className="flex items-center justify-center min-h-40 px-4 mt-8">
          <div className="bg-white rounded-2xl border border-red-100 shadow-lg p-8 max-w-md w-full text-center">
            <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-6">
              <FontAwesomeIcon
                className="w-8 h-8 text-red-500"
                icon={faExclamationTriangle}
              />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-3">
              Something went wrong
            </h3>
            <p className="text-gray-600 mb-6 leading-relaxed">
              We couldn&apos;t load the educational videos. This might be a
              temporary issue.
            </p>
            <button
              className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-6 py-3 rounded-xl font-medium transition-all duration-200 shadow-lg hover:shadow-xl"
              onClick={() => window.location.reload()}
            >
              <FontAwesomeIcon className="w-4 h-4" icon={faSpinner} />
              Try Again
            </button>
            <p className="text-xs text-gray-400 mt-4">
              If the problem persists, please contact support.
            </p>
          </div>
        </div>
      )}

      {educationalVideoData && (
        <div className="py-12 px-4 max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h6 className="uppercase tracking-widest text-xs font-bold text-blue-600 mb-4">
              educational content
            </h6>
            <h2 className="text-3xl font-extrabold tracking-wide text-primary mb-4">
              Your Personalized Videos
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto leading-relaxed">
              Watch these short videos to understand your specific findings and
              what they mean for your health.
            </p>
          </div>

          <div className="grid gap-8 md:gap-12">
            {educationalVideoData.videos.map((video, index) => (
              <div
                key={video.id || index}
                className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden"
              >
                <div className="aspect-video w-full">
                  <iframe
                    allowFullScreen
                    allow="autoplay; encrypted-media; picture-in-picture; fullscreen; clipboard-write;"
                    className="w-full h-full"
                    frameBorder="0"
                    loading="lazy"
                    referrerPolicy="origin"
                    src={video.VideoLink}
                    title={video.VideoTitle || `Educational Video ${index + 1}`}
                  />
                </div>
                {video.VideoTitle && (
                  <div className="p-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      {video.VideoTitle}
                    </h3>
                    {video.description && (
                      <p className="text-gray-600 text-sm leading-relaxed">
                        {video.description}
                      </p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-col gap-4 py-16 px-4 max-w-5xl mx-auto text-center">
        <h6 className="uppercase tracking-widest text-xs font-bold text-blue-600">
          Your Report Was Interpreted By
        </h6>
        <h2 className="text-3xl font-extrabold tracking-wide text-primary">
          Meet Your Radiologist
        </h2>

        {isEducationalVideoDataLoading ? (
          <div className="bg-white rounded-xl p-12 shadow-sm border max-w-3xl border-gray-300 mx-auto">
            <div className="flex items-center justify-center">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3"></div>
                <p className="text-gray-600 text-sm">
                  Loading radiologist information...
                </p>
              </div>
            </div>
          </div>
        ) : !educationalVideoData?.radiologistProfile ? (
          <div className="bg-white rounded-xl p-12 shadow-sm border max-w-3xl border-gray-300 mx-auto">
            <div className="text-center">
              <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FontAwesomeIcon
                  className="w-6 h-6 text-gray-400"
                  icon={faExclamationTriangle}
                />
              </div>
              <h3 className="text-lg italic text-gray-400 mb-2">
                Radiologist Information Unavailable
              </h3>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl p-6 shadow-sm border max-w-3xl border-gray-300 mx-auto">
            <div className="flex gap-6">
              <div className="relative flex-shrink-0">
                <img
                  alt={`${educationalVideoData?.radiologistProfile?.FirstName} ${educationalVideoData?.radiologistProfile?.MiddleName ? educationalVideoData.radiologistProfile.MiddleName + " " : ""}${educationalVideoData?.radiologistProfile?.LastName}`}
                  className="w-20 h-20 rounded-full object-cover border-blue-950 border-2 shadow-sm"
                  src={educationalVideoData?.radiologistProfile?.ImageUrl}
                />
              </div>

              {/* Profile Content */}
              <div className="flex-1 min-w-0 text-left">
                <div className="mb-2">
                  <p className="text-[10px] font-bold text-blue-600 uppercase tracking-widest mb-1">
                    {educationalVideoData?.radiologistProfile?.Title}
                  </p>
                  <h3 className="text-xl font-bold text-blue-950 mb-3">
                    {educationalVideoData?.radiologistProfile?.FirstName}{" "}
                    {educationalVideoData?.radiologistProfile?.MiddleName
                      ? educationalVideoData.radiologistProfile.MiddleName + " "
                      : ""}
                    {educationalVideoData?.radiologistProfile?.LastName}
                    {educationalVideoData?.radiologistProfile?.Credentials
                      ? ", " +
                        educationalVideoData.radiologistProfile.Credentials
                      : ""}
                  </h3>
                </div>

                {/* Badges */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {educationalVideoData?.radiologistProfile?.Specialities &&
                    educationalVideoData.radiologistProfile.Specialities.split(
                      ","
                    ).map((specialty, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-blue-700 text-white"
                      >
                        {specialty.trim()}
                      </span>
                    ))}
                  {educationalVideoData?.radiologistProfile?.Certifications &&
                    educationalVideoData.radiologistProfile.Certifications.split(
                      ","
                    ).map((certification, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium border border-gray-300 text-blue-950"
                      >
                        {certification.trim()}
                      </span>
                    ))}
                </div>

                {/* Description */}
                <p className="text-sm text-gray-600/90 leading-relaxed mb-4">
                  {educationalVideoData?.radiologistProfile?.Introduction}
                </p>

                {/* Credentials */}
                <div className="flex flex-wrap gap-3 mb-4 text-xs">
                  {educationalVideoData?.radiologistProfile?.Highlights &&
                    educationalVideoData.radiologistProfile.Highlights.split(
                      ","
                    ).map((highlight, index) => (
                      <span
                        key={index}
                        className="flex items-center gap-1 text-blue-950 border border-gray-300 font-medium bg-blue-50 px-2 py-1 rounded-md"
                      >
                        {highlight.trim()}
                      </span>
                    ))}
                </div>

                {/* View Profile Link */}
                <a
                  className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 text-xs font-bold transition-colors"
                  href={
                    educationalVideoData?.radiologistProfile?.ProfileUrl || "#"
                  }
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  View full profile
                  <FontAwesomeIcon
                    className="w-3 h-3"
                    icon={faArrowUpRightFromSquare}
                  />
                </a>
              </div>

              <div className="w-20" />
            </div>
          </div>
        )}
      </div>

      <div className="flex flex-col gap-4 py-16 px-4 max-w-5xl mx-auto text-center">
        <h6 className="uppercase tracking-widest text-xs font-bold text-blue-600">
          quick feedback
        </h6>
        <h2 className="text-3xl font-extrabold tracking-wide text-primary">
          How was your experience?
        </h2>
        <h6 className="text-sm text-gray-500 leading-relaxed">
          Takes 30 seconds - completely anonymous.
        </h6>
      </div>

      {!isSubmitted ? (
        <Formik
          initialValues={{
            experience: 0,
            report_clarity: 0,
            comments: "",
          }}
          validationSchema={feedbackSchema}
          onSubmit={async (values, { setSubmitting, resetForm }) => {
            if (isFeedbackSubmitting) return
            try {
              await submitFeedback(
                {
                  OverallExperience: values.experience,
                  ReportClarity: values.report_clarity,
                  Comments: values.comments,
                  AccessionNo: educationalVideoData.AccessionNo,
                  MessageId: educationalVideoData.MessageId,
                  InboundMessageId: educationalVideoData.InboundMessageId,
                },
                {
                  onSuccess: () => {
                    setSubmitting(false)
                    resetForm()
                    setIsSubmitted(true)
                  },
                  onError: (error) => {
                    setSubmitting(false)
                    console.error("Failed to submit feedback:", error)
                  },
                }
              )
            } catch (error) {
              setSubmitting(false)
              console.error("Failed to submit feedback:", error)
            }
          }}
        >
          {({ values, setFieldValue, isValid, dirty }) => (
            <Form className="bg-white rounded-xl p-6 shadow-sm border max-w-3xl border-gray-300 mx-auto mb-8">
              <div className="space-y-6">
                <StarRating
                  label="Overall experience"
                  name="experience"
                  value={values.experience}
                  onChange={setFieldValue}
                />
                <ErrorMessage
                  className="text-red-500 text-xs mt-1"
                  component="div"
                  name="experience"
                />

                <StarRating
                  label="Report clarity"
                  name="report_clarity"
                  value={values.report_clarity}
                  onChange={setFieldValue}
                />
                <ErrorMessage
                  className="text-red-500 text-xs mt-1"
                  component="div"
                  name="report_clarity"
                />

                <div className="flex flex-col gap-2">
                  <Field
                    as="textarea"
                    className="w-full p-3 border border-gray-300 rounded-lg resize-none h-24 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    maxLength={500}
                    name="comments"
                    placeholder="Anything you'd like us to know... (optional)"
                  />
                  <div className="text-right text-xs text-gray-400">
                    {values.comments.length}/500
                  </div>
                  <ErrorMessage
                    className="text-red-500 text-xs"
                    component="div"
                    name="comments"
                  />
                </div>

                <p className="text-xs text-gray-500 text-left">
                  Your feedback is anonymous. We do not collect your name,
                  email, or any identifying information.
                </p>

                <button
                  className={`w-full py-3 px-4 rounded-lg font-medium text-white transition-colors ${
                    isValid &&
                    dirty &&
                    values.experience > 0 &&
                    values.report_clarity > 0
                      ? "bg-blue-600 hover:bg-blue-700"
                      : "bg-gray-400 cursor-not-allowed"
                  }`}
                  disabled={
                    isFeedbackSubmitting ||
                    !isValid ||
                    !dirty ||
                    values.experience === 0 ||
                    values.report_clarity === 0
                  }
                  type="submit"
                >
                  {isFeedbackSubmitting ? "Submitting" : "Submit Feedback"}{" "}
                  {isFeedbackSubmitting && (
                    <FontAwesomeIcon
                      className="w-4 h-4 animate-spin ml-2 text-white"
                      icon={faSpinner}
                    />
                  )}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      ) : (
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-300 max-w-3xl mx-auto mb-8">
          <div className="flex flex-col items-center justify-center space-y-4 py-8">
            <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center">
              <FontAwesomeIcon className="w-6 h-6 text-white" icon={faCheck} />
            </div>
            <h3 className="text-lg font-bold text-primary">Thank you!</h3>
            <p className="text-sm text-gray-600/80 text-center">
              Your feedback helps us improve.
            </p>
          </div>
        </div>
      )}

      {/* <div className="flex flex-col gap-4 py-16 px-4 max-w-2xl mx-auto text-center">
        <h6 className="uppercase tracking-widest text-xs font-bold text-blue-600">
          common questions
        </h6>
        <h2 className="text-3xl font-extrabold tracking-wide text-primary">
          Frequently Asked Questions
        </h2>
        <h6 className="text-sm text-gray-500 leading-relaxed">
          Quick answers about your findings and next steps.
        </h6>
        {FAQs.map((faq, index) => {
          const isExpanded = expandedItems.has(index)
          return (
            <div
              key={index}
              className="text-left bg-gray-100 rounded-xl shadow-sm border border-gray-300"
            >
              <div
                className="flex justify-between border-b border-blue-100 p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => toggleExpanded(index)}
              >
                <h3 className="text-sm md:text-base font-semibold text-primary">
                  {faq.question}
                </h3>
                <FontAwesomeIcon
                  className={`w-2 h-2 cursor-pointer rounded-full p-2 transition-all duration-300 ${
                    isExpanded
                      ? "bg-blue-600 text-gray-50 transform rotate-45"
                      : "bg-blue-200 text-primary hover:text-primary/80"
                  }`}
                  icon={faPlus}
                />
              </div>
              {isExpanded && (
                <p className="text-gray-700/80 text-xs md:text-sm p-4 leading-relaxed whitespace-pre-wrap animate-fadeIn">
                  {faq.answer}
                </p>
              )}
            </div>
          )
        })}
      </div> */}

      <div className="bg-primary py-12 px-2 mb-8 text-center text-white rounded-3xl max-w-5xl mx-auto">
        <h2 className="text-xl md:text-3xl font-bold mb-4">
          Want a Specialist&apos;s Second Opinion?
        </h2>
        <p className="text-xs text-gray-300 mb-8 max-w-xl mx-auto">
          Upload your images and get a written report from a
          subspecialty-trained radiologist — delivered in 24–48 hours.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <a
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl flex items-center gap-2 font-medium"
            href="https://www.expertradiology.com/solutions/attorneys/"
          >
            <span className="material-icons text-xs">check_circle</span>
            Get a Second Opinion
          </a>
          <a
            className="border border-gray-600 hover:border-gray-400 text-white px-6 py-3 rounded-xl flex items-center gap-3 transition-colors"
            href="https://www.expertradiology.com/solutions/clinicians"
          >
            <span className="text-gray-400 text-xs font-medium italic font-serif">
              PrecisionPlus v3™
            </span>
            <span className="text-gray-300 font-medium">Learn more →</span>
          </a>
        </div>
      </div>

      <footer className="bg-primary text-white text-center py-8 px-4">
        <div className="flex items-center justify-center gap-4 mb-6">
          <img alt="Expert Radiology" className="h-8" src="/logo.png" />
          <div className="w-px h-6 bg-gray-600"></div>
          <span className="text-gray-400 text-xs font-bold italic font-serif">
            PrecisionPlus v3™
          </span>
        </div>
        <p className="text-gray-500 text-xs mb-4 max-w-xl mx-auto leading-relaxed">
          These videos are for educational purposes only and do not constitute
          medical advice. Always consult your treating physician regarding your
          specific condition and treatment options.
        </p>
        <p className="text-xs mb-4">
          <a
            className="text-blue-500 hover:text-blue-300"
            href="https://www.expertradiology.com"
          >
            expertradiology.com
          </a>
          <span className="text-gray-400 mx-2">·</span>
          <a
            className="text-blue-500 hover:text-blue-300"
            href="https://www.expertradiology.com/solutions/attorneys/"
          >
            Second Opinions
          </a>
          <span className="text-gray-400 mx-2">·</span>
          <a
            className="text-blue-500 hover:text-blue-300"
            href="tel:4159002000"
          >
            415-900-2000
          </a>
        </p>
        <p className="text-[10px] text-gray-400/40">
          © {new Date().getFullYear()} Expert Radiology Management Services,
          LLC. All rights reserved.
          <span className="mx-2">·</span>
          HIPAA Compliant
        </p>
      </footer>
    </div>
  )
}

export default EducationalVideos
